import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertTriangle, Users, Heart, Brain, Mail, Phone } from 'lucide-react';
import SabiwabeeLogo from '../components/SabiwabeeLogo';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2 text-gray-900 hover:text-blue-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                <SabiwabeeLogo className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Sabiwabee</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
            </div>
            <p className="text-gray-600 text-lg">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to Sabiwabee. These Terms of Service ("Terms") govern your use of our AI-powered mental health platform 
              and services. By accessing or using Sabiwabee, you agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Sabiwabee provides AI-powered mental health support, assessments, journaling tools, meditation guidance, 
              and community features. These services are designed to complement, not replace, professional mental health care.
            </p>
          </section>

          {/* Important Disclaimers */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Important Disclaimers</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                Medical Disclaimer
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Sabiwabee is not a substitute for professional medical or mental health care.</strong> Our AI-powered 
                services are designed to provide support and guidance but should not replace consultation with qualified 
                healthcare professionals.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>If you are experiencing a mental health crisis, please contact emergency services immediately</li>
                <li>Always consult with qualified healthcare providers for medical advice</li>
                <li>Our AI recommendations are not medical diagnoses or treatment plans</li>
                <li>We do not guarantee specific outcomes from using our services</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-yellow-600" />
                Emergency Situations
              </h3>
              <p className="text-gray-700 leading-relaxed">
                If you are experiencing thoughts of self-harm, suicide, or are in immediate danger, please:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6 mt-3">
                <li>Call 911 or your local emergency number</li>
                <li>Contact the National Suicide Prevention Lifeline at 988</li>
                <li>Seek immediate professional medical attention</li>
                <li>Do not rely solely on our platform for crisis intervention</li>
              </ul>
            </div>
          </section>

          {/* Acceptance of Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By using Sabiwabee, you acknowledge that you have read, understood, and agree to be bound by these Terms. 
              If you do not agree to these Terms, you must not use our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of significant changes, 
              and continued use of our services after such changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Eligibility */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Eligibility</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                To use Sabiwabee, you must:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>Be at least 13 years of age (or the age of majority in your jurisdiction)</li>
                <li>Have the legal capacity to enter into these Terms</li>
                <li>Provide accurate and complete registration information</li>
                <li>Not be prohibited from using our services under applicable law</li>
                <li>Use our services in compliance with these Terms and applicable laws</li>
              </ul>
            </div>
          </section>

          {/* Services Description */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Services Description</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Brain className="w-5 h-5 mr-2 text-blue-600" />
                  AI-Powered Services
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
                  <li>AI therapy conversations and support</li>
                  <li>Mental health assessments and insights</li>
                  <li>Personalized recommendations</li>
                  <li>Progress tracking and analytics</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Heart className="w-5 h-5 mr-2 text-purple-600" />
                  Wellness Tools
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
                  <li>Digital journaling with AI insights</li>
                  <li>Meditation and mindfulness guidance</li>
                  <li>Peer support community forums</li>
                  <li>Therapist handover services</li>
                </ul>
              </div>
            </div>
          </section>

          {/* User Accounts */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Accounts</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                When creating an account with Sabiwabee, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your account information</li>
                <li>Keep your account credentials secure and confidential</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
                <li>Not share your account with others</li>
              </ul>
            </div>
          </section>

          {/* Acceptable Use */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptable Use</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">You agree to use Sabiwabee only for lawful purposes and in accordance with these Terms. You agree not to:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                  <li>Use our services for any illegal or unauthorized purpose</li>
                  <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                  <li>Interfere with or disrupt the operation of our platform</li>
                  <li>Share harmful, offensive, or inappropriate content</li>
                  <li>Impersonate others or provide false information</li>
                  <li>Use automated systems to access our services</li>
                  <li>Attempt to reverse engineer or copy our technology</li>
                  <li>Harass, abuse, or harm other users</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Community Guidelines</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  When participating in our community forums, please:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                  <li>Be respectful and supportive of other members</li>
                  <li>Share experiences constructively and helpfully</li>
                  <li>Respect others' privacy and confidentiality</li>
                  <li>Report inappropriate behavior to our moderation team</li>
                  <li>Remember that this is a mental health support community</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Privacy and Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy and Data Protection</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your privacy is important to us. Our collection, use, and protection of your personal information 
              is governed by our Privacy Policy, which is incorporated into these Terms by reference.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our services, you consent to the collection and use of your information as described in our Privacy Policy.
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Sabiwabee and its content, features, and functionality are owned by us and are protected by copyright, 
                trademark, and other intellectual property laws.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You retain ownership of content you create and share on our platform, but you grant us a license to use, 
                store, and display that content in connection with providing our services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You may not reproduce, distribute, modify, or create derivative works of our content without our express written consent.
              </p>
            </div>
          </section>

          {/* Subscription and Payment */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Subscription and Payment</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Sabiwabee offers both free and premium subscription services. Premium features require payment of applicable fees.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>Subscription fees are billed in advance on a recurring basis</li>
                <li>You may cancel your subscription at any time</li>
                <li>Refunds are provided in accordance with our refund policy</li>
                <li>We reserve the right to change subscription fees with notice</li>
                <li>Payment processing is handled by secure third-party providers</li>
              </ul>
            </div>
          </section>

          {/* Disclaimers and Limitations */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimers and Limitations</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Service Availability</h3>
                <p className="text-gray-700 leading-relaxed">
                  We strive to provide reliable service but cannot guarantee uninterrupted access. Our services are provided 
                  "as is" and "as available" without warranties of any kind.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Limitation of Liability</h3>
                <p className="text-gray-700 leading-relaxed">
                  To the maximum extent permitted by law, Sabiwabee shall not be liable for any indirect, incidental, 
                  special, consequential, or punitive damages arising from your use of our services.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Indemnification</h3>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify and hold harmless Sabiwabee from any claims, damages, or expenses arising from 
                  your use of our services or violation of these Terms.
                </p>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                We may terminate or suspend your account and access to our services at any time, with or without cause, 
                with or without notice, effective immediately.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Upon termination, your right to use our services will cease immediately. We may delete your account 
                and data in accordance with our data retention policies.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You may terminate your account at any time by contacting us or using the account deletion feature in your settings.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms or your use 
              of our services shall be resolved in the courts of the United States.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of significant changes 
              through our platform or by email. Your continued use of our services after such changes constitutes 
              acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">legal@sabiwabee.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">1-800-SABIWABEE</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Legal Department: legal@sabiwabee.com</span>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 text-center">
            <p className="text-gray-600">
              These Terms of Service are effective as of {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} and will remain in effect except with respect to any changes in its provisions in the future.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService; 